import { Box, SxProps, Theme, Typography } from "@mui/material";
import { FC, ReactNode } from "react";
import { ICompanyVerificationStatusesConfig } from "../../network";
import {
  getStatusIcon,
  statusIcons,
  statusesModel,
  VerificationStatus,
} from "../../utils/getStatus";

export type VerificationStatusBadgeSize = "small" | "medium" | "large";

type VerificationStatusBadgeProps = {
  status?: VerificationStatus | string | null;
  statusesConfig?: ICompanyVerificationStatusesConfig;
  size?: VerificationStatusBadgeSize;
  suffix?: ReactNode;
  sx?: SxProps<Theme>;
};

const DEFAULT_STATUS_COLOR = "#6D7580";

const sizeStyles: Record<
  VerificationStatusBadgeSize,
  {
    fontSize: string;
    iconSize: number;
    lineHeight: string;
    padding: string;
    spacing: number;
  }
> = {
  small: {
    fontSize: "0.75rem",
    iconSize: 16,
    lineHeight: "16px",
    padding: "3px 8px",
    spacing: 0.5,
  },
  medium: {
    fontSize: "0.875rem",
    iconSize: 20,
    lineHeight: "20px",
    padding: "5px 10px",
    spacing: 0.625,
  },
  large: {
    fontSize: "1rem",
    iconSize: 24,
    lineHeight: "24px",
    padding: "6px 12px",
    spacing: 0.75,
  },
};

export const VerificationStatusBadge: FC<VerificationStatusBadgeProps> = ({
  status,
  statusesConfig,
  size = "medium",
  suffix,
  sx,
}) => {
  const statusKey = status as VerificationStatus | undefined;
  const statusConfig = statusKey ? statusesConfig?.object[statusKey] : undefined;
  const fallbackStatus = statusKey ? statusesModel[statusKey] : undefined;
  const color =
    statusConfig?.color || fallbackStatus?.textColor || DEFAULT_STATUS_COLOR;
  const label =
    statusConfig?.displayName || fallbackStatus?.name || status || "Unknown";
  const hasIcon = !!statusKey && statusKey in statusIcons;
  const styles = sizeStyles[size];

  return (
    <Box
      sx={[
        {
          alignItems: "center",
          backgroundColor: `${color}1A`,
          borderRadius: "6px",
          color,
          display: "inline-flex",
          gap: styles.spacing,
          maxWidth: "100%",
          padding: styles.padding,
          width: "fit-content",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {hasIcon && (
        <Box
          component="span"
          sx={{
            alignItems: "center",
            display: "inline-flex",
            flex: "0 0 auto",
            height: styles.iconSize,
            width: styles.iconSize,
          }}
        >
          {getStatusIcon(statusKey!, {
            color,
            fill: color,
            sx: {
              color,
              fontSize: styles.iconSize,
              height: styles.iconSize,
              width: styles.iconSize,
            },
          })}
        </Box>
      )}
      <Typography
        component="span"
        sx={{
          color: "inherit",
          fontSize: styles.fontSize,
          fontWeight: 700,
          lineHeight: styles.lineHeight,
        }}
      >
        {label}
        {suffix ? <> {suffix}</> : null}
      </Typography>
    </Box>
  );
};
